import { useMemo } from 'react'
import { AssetsModule } from '@/core/modules/digital-footprint/assets'
import { IssuesModule } from '@/core/modules/issues'
import {
    assetNameColumn,
    classificationColumn,
    hasIpData,
    hasMalwarePathData,
    ipColumn,
    malwarePathsColumn,
} from '@ui/drawer/common/AffectedAssetsTable'
import {
    TSelectableAsset,
    getSelectableAssets,
    getSelectableTechnologies,
} from '@ui/drawer/common/useIssueResolution'
import { useIssueDrawer } from '@ui/drawer/common/useIssueDrawer'

export type TIssueDetailsRow = TSelectableAsset & { technology?: TTechnology }

type TIssueDetailsIssue =
    | TGeneralIssue
    | TCloudAppIssue
    | TDeviceInfostealerIssue

const DOMAIN_ASSET_TYPES: TAssetType[] = ['domain', 'subdomain', 'email']

const buildTechnologyRow = (technology: TTechnology): TIssueDetailsRow => ({
    id: `technology-${technology.id}`,
    hostname: technology.name,
    asset: {
        id: String(technology.id),
        hostname: technology.name,
        asset_type: 'integration',
    } as Partial<TAsset>,
    payload: {
        name: technology.name,
        type: 'integration' as TAssetType,
        external_manager_issue_id: null,
    },
    technology,
})

const buildIssueDetailsRows = (issue: TIssue): TIssueDetailsRow[] => [
    ...getSelectableAssets(issue),
    ...getSelectableTechnologies(issue).map(buildTechnologyRow),
]

const enrichRowsWithDeviceIp = (
    rows: TIssueDetailsRow[]
): TIssueDetailsRow[] => {
    const ipByDeviceId = new Map(
        AssetsModule.getFullData().map((device) => [
            device.id,
            device.ip_addresses,
        ])
    )
    return rows.map((row) => {
        const asset = row.asset as Partial<TDevice>
        if (asset.ip_addresses?.length) {
            return row
        }
        const ip_addresses = ipByDeviceId.get(row.asset.id ?? '')
        if (!ip_addresses?.length) {
            return row
        }
        return { ...row, asset: { ...row.asset, ip_addresses } }
    })
}

export const useIssueDetails = () => {
    const { issue, rows, selectedIds, setSelectedIds, resolution } =
        useIssueDrawer<TIssueDetailsIssue, TIssueDetailsRow>({
            store: IssuesModule,
            buildRows: buildIssueDetailsRows,
        })

    const enrichedRows = useMemo(() => enrichRowsWithDeviceIp(rows), [rows])

    const columns = useMemo(() => {
        const dataColumns = [assetNameColumn()]
        if (hasIpData(enrichedRows)) {
            dataColumns.push(ipColumn())
        }
        if (hasMalwarePathData(enrichedRows)) {
            dataColumns.push(malwarePathsColumn())
        }
        const isDomainAssetsTable =
            dataColumns.length === 1 &&
            enrichedRows.length > 0 &&
            enrichedRows.every(({ asset }) =>
                DOMAIN_ASSET_TYPES.includes(asset.asset_type as TAssetType)
            )
        if (isDomainAssetsTable) {
            dataColumns.push(classificationColumn())
        }
        return dataColumns
    }, [enrichedRows])

    const selection = useMemo(() => {
        const selectedRows = enrichedRows.filter(({ id }) =>
            selectedIds.includes(id)
        )
        return {
            assets: selectedRows
                .filter(({ technology }) => !technology)
                .map(({ payload }) => payload),
            technologies: selectedRows
                .map(({ technology }) => technology)
                .filter(
                    (technology): technology is TTechnology => !!technology
                ),
        }
    }, [enrichedRows, selectedIds])

    const selectionDisabled = issue.status !== 'open' || resolution.loading

    const isTechnologyAssetsTable =
        enrichedRows.length > 0 &&
        enrichedRows.every(({ technology }) => !!technology)

    const assetsTestIdPrefix =
        issue.issue_type === 'DEVICE_INFOSTEALER'
            ? 'device-infostealer-assets'
            : isTechnologyAssetsTable
              ? 'technology-assets'
              : 'affected-assets'

    return {
        issue,
        enrichedRows,
        columns,
        selectedIds,
        setSelectedIds,
        resolution,
        selection,
        selectionDisabled,
        assetsTestIdPrefix,
    }
}
